import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FileText, Plus, Trash2, Info, Tag, Grid3X3, Lightbulb } from 'lucide-react';
import { defaultContentCategories, tokenizeAll, findCoOccurrences, extractQuotesWithKeywords, generateAPAInterpretation } from './qualitativeUtils';

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

const ContentAnalysis = ({ texts, surveyId }) => {
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem(`content-categories-${surveyId}`);
    return saved ? JSON.parse(saved) : defaultContentCategories;
  });
  const [newCategory, setNewCategory] = useState({ name: '', keywords: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const saveCategories = (cats) => {
    setCategories(cats);
    localStorage.setItem(`content-categories-${surveyId}`, JSON.stringify(cats));
  };

  const addCategory = () => {
    if (newCategory.name && newCategory.keywords) {
      const cat = {
        id: newCategory.name.toLowerCase().replace(/\s+/g, '_'),
        name: newCategory.name,
        keywords: newCategory.keywords.split(',').map(k => k.trim().toLowerCase())
      };
      saveCategories([...categories, cat]);
      setNewCategory({ name: '', keywords: '' });
      setShowAddForm(false);
    }
  };

  const removeCategory = (id) => {
    saveCategories(categories.filter(c => c.id !== id));
  };

  const analysis = useMemo(() => {
    const results = categories.map(cat => {
      const keywordSet = new Set(cat.keywords);
      let count = 0;
      const matchingResponses = [];

      texts.forEach((text, idx) => {
        const words = tokenizeAll(text);
        const matches = words.filter(w => keywordSet.has(w)).length;
        if (matches > 0) {
          count += matches;
          matchingResponses.push({ text, matches, index: idx });
        }
      });

      return {
        ...cat,
        count,
        responseCount: matchingResponses.length,
        percentage: texts.length > 0 ? Math.round((matchingResponses.length / texts.length) * 100) : 0,
        topResponses: matchingResponses.sort((a, b) => b.matches - a.matches).slice(0, 3)
      };
    });

    const totalCoded = results.reduce((sum, r) => sum + r.count, 0);
    const coOccurrences = findCoOccurrences(texts, categories);

    const topCategory = results.sort((a, b) => b.count - a.count)[0];

    return {
      categories: results.sort((a, b) => b.count - a.count),
      totalCoded,
      totalResponses: texts.length,
      categoryCount: categories.length,
      coOccurrences,
      topCategory: topCategory ? {
        name: topCategory.name,
        count: topCategory.count,
        percentage: totalCoded > 0 ? Math.round((topCategory.count / totalCoded) * 100) : 0
      } : null
    };
  }, [texts, categories]);

  const interpretation = useMemo(() => 
    generateAPAInterpretation('content', analysis),
    [analysis]
  );

  const coOccurrenceData = useMemo(() => {
    return Object.entries(analysis.coOccurrences)
      .map(([key, count]) => {
        const [cat1, cat2] = key.split('-');
        const cat1Name = categories.find(c => c.id === cat1)?.name || cat1;
        const cat2Name = categories.find(c => c.id === cat2)?.name || cat2;
        return { pair: `${cat1Name} + ${cat2Name}`, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [analysis.coOccurrences, categories]);

  const contentSummary = useMemo(() => {
    const topCategories = analysis.categories.slice(0, 3);
    const topCatNames = topCategories.map(c => `"${c.name}" (${c.count} instances, ${c.percentage}% coverage)`).join(', ');
    const coOccurrenceCount = Object.keys(analysis.coOccurrences).length;
    
    return `This content analysis systematically examined ${analysis.totalResponses} text segments using a categorical coding framework with ${analysis.categoryCount} predefined categories. A total of ${analysis.totalCoded} coded instances were identified across all categories. The most prevalent categories were: ${topCatNames}. ${analysis.topCategory ? `The dominant category "${analysis.topCategory.name}" accounts for ${analysis.topCategory.percentage}% of all coded content, suggesting this theme is central to the dataset.` : ''} Co-occurrence analysis identified ${coOccurrenceCount} category pairs that appear together within the same responses, revealing potential thematic relationships. ${coOccurrenceCount > 5 ? 'The high number of co-occurrences suggests rich interconnections between themes.' : 'The co-occurrence patterns may indicate underlying conceptual frameworks.'} Researchers should examine whether these categorical distributions align with theoretical expectations, and consider triangulating with other qualitative methods such as narrative or discourse analysis to deepen understanding of how these content categories manifest in participant experiences and perspectives.`;
  }, [analysis]);

  return (
    <div className="content-analysis">
      <div className="analysis-header">
        <h4><FileText size={20} /> Content Analysis</h4>
        <p>Categorize and count themes using a codebook approach</p>
      </div>

      <div className="analysis-guidance">
        <Lightbulb size={18} />
        <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
      </div>

      <div className="category-manager">
        <div className="category-header">
          <h5><Tag size={16} /> Categories ({categories.length})</h5>
          <button className="btn-add" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus size={16} /> Add Category
          </button>
        </div>

        {showAddForm && (
          <div className="add-category-form">
            <input
              type="text"
              placeholder="Category name"
              value={newCategory.name}
              onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Keywords (comma-separated)"
              value={newCategory.keywords}
              onChange={e => setNewCategory({ ...newCategory, keywords: e.target.value })}
            />
            <button onClick={addCategory}>Add</button>
            <button onClick={() => setShowAddForm(false)}>Cancel</button>
          </div>
        )}

        <div className="category-list">
          {categories.map(cat => (
            <div 
              key={cat.id} 
              className={`category-chip ${selectedCategory === cat.id ? 'selected' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
            >
              <span className="cat-name">{cat.name}</span>
              <span className="cat-count">{analysis.categories.find(c => c.id === cat.id)?.count || 0}</span>
              <button 
                className="btn-remove" 
                onClick={(e) => { e.stopPropagation(); removeCategory(cat.id); }}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="analysis-results">
        <div className="stats-row">
          <div className="stat-box">
            <div className="stat-value">{analysis.totalResponses}</div>
            <div className="stat-label">Responses Analyzed</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{analysis.totalCoded}</div>
            <div className="stat-label">Total Coded Instances</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{analysis.categoryCount}</div>
            <div className="stat-label">Categories Used</div>
          </div>
        </div>

        <div className="chart-section">
          <h5>Category Distribution</h5>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analysis.categories} layout="vertical" margin={{ left: 120, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [`${value} instances`, 'Count']} />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-section">
          <h5>Response Coverage by Category</h5>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analysis.categories.filter(c => c.count > 0)}
                dataKey="responseCount"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {analysis.categories.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {coOccurrenceData.length > 0 && (
          <div className="chart-section">
            <h5><Grid3X3 size={16} /> Category Co-occurrence</h5>
            <p className="chart-desc">Categories that appear together in the same response</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={coOccurrenceData} margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="pair" tick={{ fontSize: 10, angle: -20 }} height={60} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {selectedCategory && (
          <div className="selected-category-detail">
            <h5>Sample Responses: {categories.find(c => c.id === selectedCategory)?.name}</h5>
            {analysis.categories.find(c => c.id === selectedCategory)?.topResponses.map((resp, idx) => (
              <div key={idx} className="sample-response">
                <span className="match-badge">{resp.matches} matches</span>
                <p>"{resp.text.substring(0, 200)}{resp.text.length > 200 ? '...' : ''}"</p>
              </div>
            ))}
          </div>
        )}

        <div className="interpretation-section">
          <h5><Info size={18} /> Analytical Summary</h5>
          <p>{contentSummary}</p>
        </div>
      </div>
    </div>
  );
};

export default ContentAnalysis;
